import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

/**
 * The component vocabulary. One shape per job, used everywhere.
 *
 * Zero radius throughout — the brand's most recognisable trait, and the reason
 * the Tailwind theme has no radius scale to reach for by accident. States are
 * printed marks rather than colour washes: a hairline at rest, a filled ground
 * when live, a struck rule when spent.
 */

// ------------------------------------------------------------------ label ---

export function Label({ children, as: As = "div" }: { children: ReactNode; as?: "div" | "span" | "h2" | "h3" }) {
  return <As className="label">{children}</As>;
}

// ------------------------------------------------------------------ marks ---

export type MarkTone = "rest" | "live" | "success" | "warning" | "danger" | "spent";

const MARK_CLASS: Record<MarkTone, string> = {
  rest: "mark",
  live: "mark mark--live",
  success: "mark mark--success",
  warning: "mark mark--warning",
  danger: "mark mark--danger",
  spent: "mark mark--spent",
};

/**
 * A status, set rather than decorated.
 *
 * `tone` is the visual register; `children` is the word. They are separate
 * because the same word carries different weight in different places — a
 * deliverable "approved" is success, an assignment "declined" is not a failure.
 */
export function Mark({ tone = "rest", children }: { tone?: MarkTone; children: ReactNode }) {
  return <span className={MARK_CLASS[tone]}>{children}</span>;
}

/** The tone a deliverable status carries. */
export function deliverableTone(status: string): MarkTone {
  switch (status) {
    case "approved":
    case "released":
      return "success";
    case "in_review":
    case "awaiting_client":
      return "warning";
    case "revision_requested":
      return "danger";
    case "withdrawn":
      return "spent";
    default:
      return "rest";
  }
}

/** The tone a classification carries. Sensitivity rises; so does the tone. */
export function classificationTone(value: string): MarkTone {
  switch (value) {
    case "client_visible":
      return "live";
    case "restricted":
      return "warning";
    case "highly_restricted":
      return "danger";
    default:
      return "rest";
  }
}

// ----------------------------------------------------------------- button ---

type ButtonTone = "primary" | "secondary" | "quiet" | "danger";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: ButtonTone;
  loading?: boolean;
  children: ReactNode;
}

const BUTTON_BASE =
  "inline-flex min-h-11 items-center justify-center gap-3 border px-5 text-sm font-medium " +
  "transition-[color,background-color,border-color,transform] duration-200 ease-damped disabled:cursor-not-allowed disabled:opacity-45";

const BUTTON_TONE: Record<ButtonTone, string> = {
  primary:
    "border-ink bg-ink text-canvas hover:bg-ink/85 hover:-translate-y-px active:translate-y-0 active:bg-ink/75 disabled:hover:bg-ink disabled:hover:translate-y-0",
  secondary:
    "border-line-strong bg-transparent text-ink hover:bg-ink/[0.06] active:bg-ink/[0.1]",
  quiet:
    "border-transparent bg-transparent text-secondary hover:border-line hover:text-ink",
  danger:
    "border-danger/50 bg-transparent text-danger hover:bg-danger/[0.08] active:bg-danger/[0.14]",
};

/**
 * The button's look, for the times the right element is not a button.
 *
 * Navigation should be an `<a>`: middle-click, open-in-new-tab and the status
 * bar preview all come free, and a screen reader announces "link" rather than
 * "button" — which is the truth when the thing it does is go somewhere. But a
 * primary action still needs to look like one, and the alternative to sharing
 * these classes is pasting a forty-character Tailwind string into every screen
 * that wants it, where it then drifts.
 *
 * Only for a real `<Link>` or `<a>`. If it does not navigate, use `Button`.
 */
export function buttonClasses(tone: ButtonTone = "secondary"): string {
  return `${BUTTON_BASE} ${BUTTON_TONE[tone]}`;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { tone = "secondary", loading = false, children, className = "", disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // Loading is a disabled state with a reason, not a separate mode. The
      // label stays put so the button does not change width mid-action.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${BUTTON_BASE} ${BUTTON_TONE[tone]} ${className}`}
      {...rest}
    >
      {loading && (
        <span
          aria-hidden
          className="h-2.5 w-2.5 border border-current border-r-transparent motion-safe:animate-spin"
        />
      )}
      {children}
    </button>
  );
});

// ------------------------------------------------------------------ field ---

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, id, className = "", ...rest },
  ref,
) {
  const fieldId = id ?? `field-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const describedBy = [error ? `${fieldId}-error` : null, hint ? `${fieldId}-hint` : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="label">
        {label}
      </label>
      <input
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={
          "min-h-9 border bg-control px-2.5 text-base text-ink transition-colors duration-150 " +
          "ease-damped placeholder:text-muted hover:border-line-strong " +
          "disabled:cursor-not-allowed disabled:opacity-50 " +
          (error ? "border-danger " : "border-line ") +
          className
        }
        {...rest}
      />
      {hint && !error && (
        <p id={`${fieldId}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${fieldId}-error`} className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
});

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, hint, error, id, className = "", ...rest },
  ref,
) {
  const fieldId = id ?? `field-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const describedBy = [error ? `${fieldId}-error` : null, hint ? `${fieldId}-hint` : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="label">
        {label}
      </label>
      <textarea
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={
          "min-h-28 resize-y border bg-control px-2.5 py-2 text-base leading-[1.35rem] text-ink " +
          "transition-colors duration-150 ease-damped placeholder:text-muted hover:border-line-strong " +
          "disabled:cursor-not-allowed disabled:opacity-50 " +
          (error ? "border-danger " : "border-line ") +
          className
        }
        {...rest}
      />
      {hint && !error && (
        <p id={`${fieldId}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${fieldId}-error`} className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
  children: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, id, className = "", children, ...rest },
  ref,
) {
  const fieldId = id ?? `field-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const hintId = hint ? `${fieldId}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="label">
        {label}
      </label>
      <select
        ref={ref}
        id={fieldId}
        aria-describedby={hintId}
        className={
          "min-h-9 border border-line bg-control px-2.5 text-base text-ink transition-colors " +
          "duration-150 ease-damped hover:border-line-strong disabled:cursor-not-allowed " +
          "disabled:opacity-50 " +
          className
        }
        {...rest}
      >
        {children}
      </select>
      {hint && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}
    </div>
  );
});

// ------------------------------------------------------------------ panel ---

/**
 * Used sparingly. A region is normally defined by the rule above it; a panel is
 * for the few places something genuinely sits on a raised surface.
 */
export function Panel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`border border-line bg-panel ${className}`}>{children}</div>;
}

// --------------------------------------------------- empty · absent · fail --

/**
 * Three different claims, and the interface must not conflate them.
 *
 * `Empty` — nothing here yet, and here is what to do about it.
 * `Absent` — nothing here *for you*, phrased so it discloses nothing about
 *            whether anything exists beyond reach.
 * `Failed` — we could not ask. Never dressed as emptiness.
 */

/**
 * Nothing here yet, and what to do about it.
 *
 * Tightened after seeing it rendered: at `py-10` a page of empty regions became
 * a column of dead space, which reads as unfinished rather than calm. An empty
 * state is a sentence, not a panel.
 *
 * `title` is optional because the region heading above it often already says
 * the state — "Nothing needs you" followed by "You are up to date" says one
 * thing twice.
 */
export function Empty({
  title,
  children,
  action,
}: {
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="border-t border-line py-5">
      {title && <p className="text-base text-ink">{title}</p>}
      {children && (
        <p className={`max-w-[52ch] text-xs leading-[1.1rem] text-muted ${title ? "mt-1" : ""}`}>
          {children}
        </p>
      )}
      {action && <div className="mt-3.5">{action}</div>}
    </div>
  );
}

/**
 * Nothing here for you.
 *
 * It names nothing — not the object, not the engagement, not whether either
 * exists. The server answers 404 rather than 403 on an unpermitted read for the
 * same reason, and a message that filled in the blank would undo it.
 */
export function AbsentNotice() {
  return (
    <div className="border-t border-line py-5">
      <p className="text-base text-ink">Nothing here for you</p>
      <p className="mt-1 max-w-[52ch] text-xs leading-[1.1rem] text-muted">
        {/* Deliberately silent on whether anything exists. The server answers
            404 rather than 403 for exactly this reason. */}
        There is nothing here for you. If you were expecting something, your
        contact at Arikekpar &amp; Company can check.
      </p>
    </div>
  );
}

export function Failed({
  message,
  correlationId,
  onRetry,
}: {
  message: string;
  correlationId?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="border-t border-danger/40 py-5">
      <p className="text-base text-ink">That did not load</p>
      <p className="mt-1 max-w-[52ch] text-xs leading-[1.1rem] text-muted">{message}</p>
      {correlationId && (
        <p className="mt-1.5 font-mono text-2xs text-muted">Reference {correlationId}</p>
      )}
      {onRetry && (
        <div className="mt-3.5">
          <Button tone="secondary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------- skeleton ---

/**
 * Holds the real geometry of the row it replaces, so nothing reflows when the
 * data lands. A spinner in the middle of content is the thing this avoids.
 */
export function SkeletonRows({ rows = 4 }: { rows?: number }) {
  return (
    <div aria-busy className="animate-pulse motion-reduce:animate-none">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 border-t border-line py-3">
          <div className="h-3 w-3/12 bg-ink/[0.07]" />
          <div className="h-3 w-2/12 bg-ink/[0.05]" />
          <div className="ml-auto h-3 w-16 bg-ink/[0.05]" />
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------- figure ------

/**
 * A number that matters, with its label beneath.
 *
 * Not a stat card: no box, no icon, no accent plane. It sits in the margin or
 * in a ruled row and the rule does the dividing.
 */
export function Figure({
  value,
  label,
  tone = "ink",
}: {
  value: ReactNode;
  label: string;
  tone?: "ink" | "danger" | "warning" | "success" | "muted";
}) {
  const colour =
    tone === "danger"
      ? "text-danger"
      : tone === "warning"
        ? "text-warning"
        : tone === "success"
          ? "text-success"
          : tone === "muted"
            ? "text-muted"
            : "text-ink";

  return (
    <div className="flex flex-col gap-0.5">
      <span data-numeric className={`font-display text-xl tracking-title ${colour}`}>
        {value}
      </span>
      <span className="label">{label}</span>
    </div>
  );
}

/**
 * Progress as a figure and a ruled bar.
 *
 * Two numbers, deliberately: the platform calculates progress from completed
 * tasks, and a Lead may post an official figure that differs. A ring cannot
 * show the difference between them, which is why there is no ring.
 */
export function Progress({ calculated, official }: { calculated: number; official?: number | null }) {
  const stated = official ?? null;
  const diverges = stated !== null && Math.abs(stated - calculated) >= 1;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <span data-numeric className="font-display text-2xl tracking-tight text-ink">
          {stated ?? calculated}%
        </span>
        <span className="label">{stated === null ? "calculated" : "official"}</span>
      </div>

      <div className="relative h-1.5 w-full border border-line bg-ink/[0.06]">
        {/* The calculated figure is always drawn. When a Lead has stated a
            different one, both are visible — the divergence is the information. */}
        <div
          className="absolute inset-y-0 left-0 bg-ink/25 transition-[width] duration-300 ease-damped"
          style={{ width: `${Math.min(100, Math.max(0, calculated))}%` }}
        />
        {stated !== null && (
          <div
            className="absolute inset-y-0 left-0 bg-sand-strong transition-[width] duration-300 ease-damped"
            style={{ width: `${Math.min(100, Math.max(0, stated))}%` }}
          />
        )}
      </div>

      {diverges && (
        <p className="text-xs text-muted">
          Completed tasks put calculated progress at {calculated}%.
        </p>
      )}
    </div>
  );
}

/**
 * Classification, offering only what this actor may actually write.
 *
 * `classification` is an ordered enum — client_visible < internal <
 * restricted < highly_restricted — and nearly every insert policy carries
 * `classification <= app.ceiling(engagement_id)`. The interface offered
 * all four everywhere, so a consultant choosing "Restricted" got the
 * database's refusal with no indication that the *classification* was the
 * objection rather than the act.
 *
 * `ceiling` comes from the server (`EngagementParts.ceiling`) rather than
 * from the session role, because the Partner case depends on a membership
 * flag on this particular engagement.
 */
const CLASSIFICATIONS = [
  { value: "client_visible", label: "Client visible" },
  { value: "internal", label: "Internal" },
  { value: "restricted", label: "Restricted" },
  { value: "highly_restricted", label: "Highly restricted" },
] as const;

export function ClassificationSelect({
  value,
  ceiling,
  onChange,
  label = "Visibility",
  hint,
}: {
  value: string;
  /** The highest the actor may write. Anything above it is not offered. */
  ceiling: string;
  onChange: (value: string) => void;
  label?: string;
  hint?: string;
}) {
  const cap = CLASSIFICATIONS.findIndex((c) => c.value === ceiling);
  // An unrecognised ceiling must not silently offer everything. Falling
  // back to the lowest is the safe direction to be wrong in.
  const allowed = CLASSIFICATIONS.slice(0, cap === -1 ? 1 : cap + 1);
  const only = allowed.length === 1;

  return (
    <Select
      label={label}
      hint={
        hint ??
        (only
          ? `Your access allows ${allowed[0]!.label.toLowerCase()} only.`
          : undefined)
      }
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {allowed.map((c) => (
        <option key={c.value} value={c.value}>
          {c.label}
        </option>
      ))}
    </Select>
  );
}
